import type { LuckFactor } from "@/lib/conditions/luck";
import { LuckGauge } from "./LuckGauge";

/* ─── DriverRow ─── */
/* Copied from UnifiedCoastal.tsx ~:73 — must stay visually identical. */
function DriverRow({
  label,
  forecastValue,
  typicalValue,
  direction,
}: {
  label: string;
  forecastValue: string;
  typicalValue: string;
  direction: "up" | "down" | "flat";
}) {
  const arrow = direction === "up" ? "▲" : direction === "down" ? "▼" : "·";
  const color =
    direction === "up"
      ? "text-emerald-600"
      : direction === "down"
        ? "text-red-600"
        : "text-text-muted";
  return (
    <div className="flex items-center justify-between py-3 border-b border-border-light last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-navy">{label}</div>
        <div className="text-[11.5px] text-text-muted">{typicalValue}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-[13px] font-semibold ${color}`} aria-hidden="true">
          {arrow}
        </span>
        <div className="font-serif text-[15px] font-semibold text-navy tabular-nums">
          {forecastValue}
        </div>
      </div>
    </div>
  );
}

/* ─── LuckDetail ─── */
/**
 * Renders the full luck detail body for a `LuckFactor`:
 * - `<LuckGauge>` (gauge SVG + its own "+x.x vs typical" chip + "Typical X/10" line)
 * - Driver rows (if any)
 * - Bullet list (if any)
 *
 * Visual layout mirrors the luck card body in UnifiedCoastal.tsx ~:670–700.
 * The gauge renders its own score annotation; this component does NOT
 * re-implement those internals.
 */
export function LuckDetail({ luck }: { luck: LuckFactor }) {
  return (
    <div className="grid md:grid-cols-[minmax(0,260px)_minmax(0,1fr)] gap-6 md:gap-12 items-start">
      <LuckGauge luck={luck} />
      <div>
        {luck.drivers.length > 0 && (
          <div className="mb-3">
            <h4 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-2">
              What&apos;s driving it
            </h4>
            {luck.drivers.map((driver) => (
              <DriverRow
                key={driver.label}
                label={driver.label}
                forecastValue={driver.forecastValue}
                typicalValue={driver.typicalValue}
                direction={driver.direction}
              />
            ))}
          </div>
        )}
        {luck.bullets.length > 0 && (
          <ul className="space-y-1.5">
            {luck.bullets.map((bullet, i) => (
              <li
                key={i}
                className="flex gap-2.5 items-start text-[14px] leading-[1.5] text-text-body"
              >
                <span
                  aria-hidden="true"
                  className={`mt-[7px] shrink-0 w-1.5 h-1.5 rounded-full ${
                    bullet.sentiment === "positive"
                      ? "bg-emerald-500"
                      : bullet.sentiment === "negative"
                        ? "bg-red-400"
                        : "bg-gold"
                  }`}
                />
                <span>{bullet.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
